import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  RefreshCw,
  Code,
  Settings,
  Loader2
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface TemplateMapping {
  id: string
  name: string
  template_type: 'label' | 'report' | 'export' | 'notification'
  template_content: string
  variables: string[]
  active: boolean
  created_at: string
  updated_at: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

const oldMockTemplateMappings: TemplateMapping[] = [
  {
    id: '1',
    name: 'Item Label Template',
    template_type: 'label',
    template_content: '{{item_name}} - {{sku}}\n{{category}} | {{condition}}\n{{location}}',
    variables: ['item_name', 'sku', 'category', 'condition', 'location'],
    active: true,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: '2',
    name: 'Inventory Report Template',
    template_type: 'report',
    template_content: 'Inventory Report\nDate: {{date}}\nTotal Items: {{total_items}}\nCategories: {{categories}}',
    variables: ['date', 'total_items', 'categories'],
    active: true,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: '3',
    name: 'Export Template',
    template_type: 'export',
    template_content: '{{sku}},{{name}},{{category}},{{condition}},{{price}},{{location}}',
    variables: ['sku', 'name', 'category', 'condition', 'price', 'location'],
    active: true,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  },
  {
    id: '4',
    name: 'Low Stock Notification',
    template_type: 'notification',
    template_content: 'Alert: {{item_name}} is running low ({{quantity}} remaining)',
    variables: ['item_name', 'quantity'],
    active: false,
    created_at: '2024-01-15T10:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  }
]

export default function TemplateMappingPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [templateMappings, setTemplateMappings] = useState<TemplateMapping[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<TemplateMapping | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'label' as TemplateMapping['template_type'],
    template_content: '',
    active: true
  })
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const fetchTemplateMappings = async () => {
    try {
      setRefreshing(true)
      const response = await fetch(`${API_BASE}/api/template-mappings-pg`)
      const data = await response.json()

      if (data.success) {
        setTemplateMappings(data.data)
      } else {
        showError('Failed to fetch template mappings', data.error)
      }
    } catch (err) {
      console.error('Error fetching template mappings:', err)
      showError('Error', 'Failed to fetch template mappings')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchTemplateMappings()
  }, [])

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim() || !formData.template_content.trim()) {
      showError('Validation Error', 'Name and template content are required')
      return
    }

    setLoading(true)

    try {
      // Extract variables from template content
      const variables = formData.template_content.match(/\{\{(\w+)\}\}/g)?.map(v => v.replace(/[{}]/g, '')) || []

      const response = await fetch(`${API_BASE}/api/template-mappings-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, variables })
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'Template mapping created successfully')
        setTemplateMappings(prev => [...prev, data.data])
        setFormData({ name: '', template_type: 'label', template_content: '', active: true })
        setShowCreateDialog(false)
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error creating template mapping:', err)
      showError('Error', 'Failed to create template mapping')
    } finally {
      setLoading(false)
    }
  }

  const handleEditTemplate = (template: TemplateMapping) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      template_type: template.template_type,
      template_content: template.template_content,
      active: template.active
    })
  }

  const handleUpdateTemplate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingTemplate) return

    setLoading(true)

    try {
      // Extract variables from template content
      const variables = formData.template_content.match(/\{\{(\w+)\}\}/g)?.map(v => v.replace(/[{}]/g, '')) || []

      const response = await fetch(`${API_BASE}/api/template-mappings-pg`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editingTemplate.id, ...formData, variables })
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'Template mapping updated successfully')
        setTemplateMappings(prev => prev.map(template =>
          template.id === editingTemplate.id ? data.data : template
        ))
        setEditingTemplate(null)
        setFormData({ name: '', template_type: 'label', template_content: '', active: true })
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error updating template mapping:', err)
      showError('Error', 'Failed to update template mapping')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template mapping?')) return

    try {
      const response = await fetch(`${API_BASE}/api/template-mappings-pg?id=${templateId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'Template mapping deleted successfully')
        setTemplateMappings(prev => prev.filter(template => template.id !== templateId))
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error deleting template mapping:', err)
      showError('Error', 'Failed to delete template mapping')
    }
  }

  const getTypeIcon = (type: TemplateMapping['template_type']) => {
    const icons = {
      label: FileText,
      report: FileText,
      export: Code,
      notification: Settings
    }
    const Icon = icons[type]
    return <Icon className="h-4 w-4" />
  }

  const getTypeColor = (type: TemplateMapping['template_type']) => {
    const colors = {
      label: '#00F0FF',
      report: '#6BCF7F',
      export: '#FF6B35',
      notification: '#B91BFF'
    }
    return colors[type]
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              TEMPLATE MAPPING
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              MANAGE TEMPLATES AND THEIR VARIABLE MAPPINGS
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: 'rgba(107, 207, 127, 0.1)',
                border: '2px solid rgba(107, 207, 127, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#6BCF7F',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              NEW TEMPLATE
            </button>
          </div>
        </div>
      </div>

      {/* Template Mappings List */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <FileText style={{ width: 20, height: 20, color: '#FF6B35' }} />
          <h3 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            background: 'linear-gradient(to right, #FF6B35, #F472B4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Template Mappings
          </h3>
        </div>

        {loading && !showCreateDialog && !editingTemplate ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 0'
          }}>
            <Loader2 style={{
              width: 32,
              height: 32,
              color: '#FF6B35',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        ) : templateMappings.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 0',
            color: '#8A95AA'
          }}>
            <FileText style={{
              width: 48,
              height: 48,
              margin: '0 auto 16px',
              opacity: 0.5
            }} />
            <p>No templates found. Create your first template to get started.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
            gap: '24px'
          }}>
            {templateMappings.map((template) => {
              const typeColor = getTypeColor(template.template_type)
              const TypeIcon = getTypeIcon(template.template_type)

              return (
                <div
                  key={template.id}
                  style={{
                    background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(36, 41, 56, 0.95) 100%)',
                    border: `2px solid ${typeColor}`,
                    borderRadius: '8px',
                    padding: '24px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 0 0px ${typeColor}40`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = typeColor
                    e.currentTarget.style.boxShadow = `0 0 20px ${typeColor}80`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = typeColor
                    e.currentTarget.style.boxShadow = `0 0 0px ${typeColor}40`
                  }}
                >
                  {/* Header with icon and title */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      {/* Type icon box */}
                      <div style={{
                        background: `${typeColor}15`,
                        border: `2px solid ${typeColor}`,
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {TypeIcon}
                      </div>

                      {/* Template name */}
                      <div>
                        <h4 style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '20px',
                          fontWeight: 700,
                          color: typeColor,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          margin: 0,
                          textShadow: `0 0 10px ${typeColor}60`
                        }}>
                          {template.name}
                        </h4>
                        <p style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '12px',
                          color: '#8A95AA',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          margin: '4px 0 0 0'
                        }}>
                          {template.template_type}
                        </p>
                      </div>
                    </div>

                    {/* Active/Inactive badge */}
                    <div style={{
                      background: template.active ? 'rgba(107, 207, 127, 0.15)' : 'rgba(138, 149, 170, 0.15)',
                      border: `2px solid ${template.active ? '#6BCF7F' : '#8A95AA'}`,
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: template.active ? '#6BCF7F' : '#8A95AA',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      boxShadow: template.active ? '0 0 10px rgba(107, 207, 127, 0.3)' : 'none'
                    }}>
                      {template.active ? 'ACTIVE' : 'INACTIVE'}
                    </div>
                  </div>

                  {/* Variables count stat */}
                  <div style={{
                    background: `${typeColor}10`,
                    border: `1px solid ${typeColor}40`,
                    borderRadius: '6px',
                    padding: '12px 16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '11px',
                        color: '#8A95AA',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontWeight: 600
                      }}>
                        VARIABLES
                      </span>
                      <span style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        color: typeColor,
                        fontWeight: 700
                      }}>
                        {template.variables.length}
                      </span>
                    </div>
                    {template.variables.length > 0 && (
                      <div style={{
                        marginTop: '8px',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#B4BCD0',
                        wordBreak: 'break-word'
                      }}>
                        {template.variables.join(', ')}
                      </div>
                    )}
                  </div>

                  {/* Content preview */}
                  <div style={{ marginBottom: '20px' }}>
                    <span style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '11px',
                      color: '#8A95AA',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      CONTENT PREVIEW
                    </span>
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      padding: '12px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: '#B4BCD0',
                      overflowX: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}>
                      {template.template_content.length > 100
                        ? `${template.template_content.substring(0, 100)}...`
                        : template.template_content
                      }
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    paddingTop: '16px',
                    borderTop: `1px solid ${typeColor}30`
                  }}>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      style={{
                        flex: 1,
                        background: 'rgba(255, 217, 61, 0.1)',
                        border: '2px solid rgba(255, 217, 61, 0.4)',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        color: '#FFD93D',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 217, 61, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 217, 61, 0.4)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 217, 61, 0.1)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <Edit style={{ width: 16, height: 16 }} />
                      EDIT
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '2px solid rgba(239, 68, 68, 0.4)',
                        borderRadius: '6px',
                        padding: '10px 16px',
                        color: '#EF4444',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.4)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                        e.currentTarget.style.boxShadow = 'none'
                      }}
                    >
                      <Trash2 style={{ width: 16, height: 16 }} />
                      DELETE
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Template Dialog */}
      {(showCreateDialog || editingTemplate) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.98) 0%, rgba(36, 41, 56, 0.98) 100%)',
            border: '2px solid #FF6B35',
            borderRadius: '8px',
            boxShadow: '0 0 40px rgba(255, 107, 53, 0.3)',
            width: '100%',
            maxWidth: '640px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Dialog Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid rgba(255, 107, 53, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {editingTemplate ? (
                  <Edit style={{ width: 20, height: 20, color: '#FF6B35' }} />
                ) : (
                  <Plus style={{ width: 20, height: 20, color: '#FF6B35' }} />
                )}
                <h2 style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#FF6B35',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  margin: 0,
                  textShadow: '0 0 10px rgba(255, 107, 53, 0.5)'
                }}>
                  {editingTemplate ? 'EDIT TEMPLATE' : 'CREATE TEMPLATE'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowCreateDialog(false)
                  setEditingTemplate(null)
                  setFormData({ name: '', template_type: 'label', template_content: '', active: true })
                }}
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#EF4444',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                  e.currentTarget.style.boxShadow = '0 0 10px rgba(239, 68, 68, 0.3)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Dialog Content */}
            <div style={{ padding: '24px' }}>
              <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}>
                {/* Template Name */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    TEMPLATE NAME *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter template name"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '2px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Template Type */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    TEMPLATE TYPE *
                  </label>
                  <select
                    value={formData.template_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_type: e.target.value as TemplateMapping['template_type'] }))}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '2px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <option value="label">Label</option>
                    <option value="report">Report</option>
                    <option value="export">Export</option>
                    <option value="notification">Notification</option>
                  </select>
                </div>

                {/* Template Content */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    TEMPLATE CONTENT *
                  </label>
                  <textarea
                    value={formData.template_content}
                    onChange={(e) => setFormData(prev => ({ ...prev, template_content: e.target.value }))}
                    placeholder="Enter template content. Use {{variable_name}} for variables."
                    required
                    rows={8}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '2px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      color: '#FFFFFF',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      outline: 'none',
                      transition: 'all 0.2s',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 10px rgba(255, 107, 53, 0.2)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginTop: '6px',
                    margin: '6px 0 0 0'
                  }}>
                    Use {'{{variable_name}}'} syntax for variables. Available variables will be auto-detected.
                  </p>
                </div>

                {/* Active Template Checkbox */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    ACTIVE TEMPLATE
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#6BCF7F'
                      }}
                    />
                    <span style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      color: '#FFFFFF',
                      fontWeight: 500
                    }}>
                      Set template as active
                    </span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  paddingTop: '20px',
                  borderTop: '1px solid rgba(255, 107, 53, 0.2)'
                }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: 'rgba(107, 207, 127, 0.1)',
                      border: '2px solid rgba(107, 207, 127, 0.4)',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      color: '#6BCF7F',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.4)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    {loading ? (
                      <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <Save style={{ width: 16, height: 16 }} />
                    )}
                    {editingTemplate ? 'UPDATE' : 'CREATE'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateDialog(false)
                      setEditingTemplate(null)
                      setFormData({ name: '', template_type: 'label', template_content: '', active: true })
                    }}
                    style={{
                      background: 'rgba(138, 149, 170, 0.1)',
                      border: '2px solid rgba(138, 149, 170, 0.4)',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      color: '#8A95AA',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
